import { logD, logE } from "@blacknut/logging/dist";
import { ModalSubscription, useModal } from "@blacknut/react-sdk";
import { useCallback, useRef } from "react";
import { LOGGING_TAG } from "src/utils/Utils";
import React from "react";
import NPSModal from "src/components/Modals/NPSModal";
import FeedbackPostSessionModal from "src/components/Modals/FeedbackPostSessionModal";
import { checkDisplayNps as apiCheckDisplayNps } from "@blacknut/javascript-sdk/dist/api/Feedback/checkDisplayNps";
import { firstValueFrom } from "rxjs";
import { postFeedback as apiPostFeedback } from "@blacknut/javascript-sdk/dist/api/Feedback/postFeedback";
import { postFeedbackSkip as apiPostFeedbackSkip } from "@blacknut/javascript-sdk/dist/api/Feedback/postFeedbackSkip";
import { addNotification } from "core/index";
import { NotificationType } from "@blacknut/javascript-sdk/dist";
import { useDispatch } from "react-redux";

const useFeedbackForms = () => {
  const { push: modalPush } = useModal();
  const modalSubscription = useRef<ModalSubscription>();
  const dispatch = useDispatch();

  const closeModal = useCallback(() => {
    modalSubscription.current?.remove();
    modalSubscription.current = undefined;
  }, []);

  const onSkipped = useCallback(
    async (type: string) => {
      try {
        await firstValueFrom(apiPostFeedbackSkip(type));
        logD(LOGGING_TAG, `${type} Form skipped`);
      } catch (error) {
        logE(LOGGING_TAG, `Error skipping ${type} Form: `, error);
      }
      closeModal();
    },
    [closeModal],
  );

  const onScore = useCallback(
    async (type: string, score: string) => {
      try {
        await firstValueFrom(apiPostFeedback(type, score));
        logD(LOGGING_TAG, `${type} Form scored: ${score}`);
        addNotification({
          type: NotificationType.SUCCESS,
          time: new Date().getTime(),
          message: "notifications.feedback",
        })(dispatch);
      } catch (error) {
        logE(LOGGING_TAG, `Error ${type} Form scored ${score}: `, error);
        addNotification({
          type: NotificationType.ERROR,
          time: new Date().getTime(),
          message: "dialogs.error.title",
        })(dispatch);
      }
      closeModal();
    },
    [closeModal, dispatch],
  );

  const checkNPSFormDisplay = useCallback(async () => {
    try {
      const checkDisplayNps = await firstValueFrom(apiCheckDisplayNps());

      if (checkDisplayNps) {
        logD(LOGGING_TAG, "nps Form displayed");

        modalSubscription.current = modalPush((props) => (
          <NPSModal
            {...props}
            onClose={() => null}
            onSkipped={onSkipped}
            onScore={onScore}
          />
        ));
      }
    } catch (error) {
      logE(LOGGING_TAG, "Error checking display nps: ", error);
    }
  }, [modalPush, onScore, onSkipped]);

  const displayPostSessionForm = useCallback(() => {
    logD(LOGGING_TAG, "postSession Form displayed");
    modalSubscription.current = modalPush((props) => (
      <FeedbackPostSessionModal
        {...props}
        onClose={() => null}
        onSkipped={onSkipped}
        onScore={onScore}
      />
    ));
  }, [modalPush, onScore, onSkipped]);

  return {
    checkNPSFormDisplay,
    displayPostSessionForm,
  };
};

export default useFeedbackForms;
